import fetcher from '@/services/fetcher'
import { InstantWinStorage } from '@/services/instant-win-storage'
import { RealTimeEvents } from '@/services/real-time-events'
import { SettingsService } from '@/services/settings'
import { SolidgateException } from '@/solidgate-exception'
import type {
  GameSessionPageData,
  InstantWinPaymentResult,
  OrderStatusUpdateEvent,
  OrderSubscription,
} from '@/types'
import {
  PaymentType,
  type TPaymentTypeValue,
  type TProductSku,
} from '@/utils/helper-objects'
import { toApiUrl, toPortalUrl } from '@/utils/url-generation-utils'
import { useGtm } from '@gtm-support/vue-gtm'
import type {
  SolidgateOneClickPaymentRequest,
  SolidgateOrderStatusEventResponse,
  SolidgatePaymentAuthorizedResponse,
  SolidgatePaymentIntentResponse,
  SolidgatePaymentIntentUserData,
  SolidgateResignIntentResponse,
} from '@solidgate/vue-sdk'
import { ref } from 'vue'

export function useSolidgate() {
  const gtm = useGtm()
  const errors = ref<string | null>(null)
  const isLoading = ref(false)

  const ValidPaymentStatuses = ['auth_ok', 'settle_ok', 'success']
  const FailedPaymentStatuses = ['auth_fail', 'failure']

  // Store active subscription
  let activeOrderSubscription: OrderSubscription | null = null

  async function mount(): Promise<any> {
    isLoading.value = true
    errors.value = null

    // Simple initialization without payment intent
    isLoading.value = false
    return Promise.resolve()
  }

  const createPaymentIntentPayload = (
    customer_email: string,
    userData: SolidgatePaymentIntentUserData
  ): SolidgateOneClickPaymentRequest => {
    return {
      currency_code: SettingsService.currencyCode,
      customer_email,
      email: customer_email,
      amount: userData.amount,
      product_amount: userData.amount,
      payment_type: userData.paymentType,
      product_sku: userData.product_sku as TProductSku,
      order_description: userData.order_description,
    }
  }

  /**
   * Create a one-click payment intent for Solidgate.
   *
   * @throws SolidgateException on failure
   * @param userData - user data for one-click payment
   * @returns The payment result
   */
  const createOneClickPayment = async (
    userData: SolidgatePaymentIntentUserData
  ) => {
    const payload = createPaymentIntentPayload(userData.email, userData)

    try {
      const { data: response, error } =
        await fetcher.post<SolidgateResignIntentResponse>(
          toPortalUrl('payments/one-click'),
          payload
        )

      if (error.value) {
        throw new Error(
          error.value.message || 'Failed to process one-click payment'
        )
      }

      isLoading.value = false

      const success = response.value?.success
      const oneClickIdempotencyKey = response.value?.csi_content.order_id

      if (success && oneClickIdempotencyKey) {
        userData.order_id = oneClickIdempotencyKey

        return response.value
      }

      throw new SolidgateException({
        message: 'One-click payment failed',
        status: response.value?.status || 500,
        isApiError: true,
        orderId: userData.order_id as string,
        originalData: response.value,
      })
    } catch (e: any) {
      isLoading.value = false
      errors.value = e.message

      // Track error with analytics
      if (gtm?.enabled) {
        gtm.trackEvent({
          event: 'payment_error',
          category: 'Checkout',
          action: 'Update Intent Error',
          label: e.message || 'Unknown error',
        })
      }

      throw e
    }
  }

  /**
   * Create a payment intent for Solidgate
   */
  const createPaymentIntent = async (
    usedCredit: number,
    customer_email: string,
    userData: SolidgatePaymentIntentUserData
  ): Promise<SolidgatePaymentIntentResponse> => {
    isLoading.value = true
    errors.value = null

    // Amount is already in cents, just handle credit conversion
    const amountInCents = userData.amount || 0
    const creditInCents = Math.round((usedCredit || 0) * 100)

    const finalAmount =
      usedCredit > 0
        ? Math.max(0, amountInCents - creditInCents)
        : Math.max(0, amountInCents)

    const postData = {
      ...userData,
      amount: finalAmount,
      currency_code: SettingsService.currencyCode,
      payment_type: userData.paymentType,
      use_credit: usedCredit > 0,
      customer_email,
    }

    try {
      const { data: response, error } = await fetcher.post(
        toApiUrl('solidgate/intent-to-pay'),
        postData
      )

      if (error.value) {
        throw new Error(
          error.value.message || 'Failed to create payment intent'
        )
      }

      isLoading.value = false
      return response.value
    } catch (e: any) {
      isLoading.value = false
      errors.value = e.message

      // Track error with analytics
      if (gtm?.enabled) {
        gtm.trackEvent({
          event: 'payment_error',
          category: 'Checkout',
          action: 'Create Intent Error',
          label: e.message || 'Unknown error',
        })
      }

      throw e
    }
  }

  /**
   * Create a token update intent for card updates
   */
  const createTokenUpdateIntent = async (
    amount: number,
    customer_email: string,
    userData: SolidgatePaymentIntentUserData
  ): Promise<SolidgatePaymentIntentResponse> => {
    isLoading.value = true
    errors.value = null

    const postData = {
      ...userData,
      amount,
      payment_type: userData.paymentType,
      customer_email,
    }

    try {
      const { data: response, error } = await fetcher.post(
        toApiUrl('solidgate/token-update-intent'),
        postData
      )

      if (error.value) {
        throw new Error(
          error.value.message || 'Failed to create token update intent'
        )
      }

      isLoading.value = false
      return response.value
    } catch (e: any) {
      isLoading.value = false
      errors.value = e.message

      // Track error with analytics
      if (gtm?.enabled) {
        gtm.trackEvent({
          event: 'payment_error',
          category: 'Checkout',
          action: 'Create Token Update Intent Error',
          label: e.message || 'Unknown error',
        })
      }

      throw e
    }
  }

  /**
   * Update a payment intent with new customer information for Solidgate
   */
  const updatePaymentIntent = async (
    paymentIntent: string,
    customer_email: string,
    userData: SolidgatePaymentIntentUserData,
    usedCredit: number
  ): Promise<SolidgatePaymentIntentResponse> => {
    isLoading.value = true
    errors.value = null

    try {
      if (!userData.order_id) {
        throw new Error('Missing order ID for payment update')
      }

      // Create payload with essential fields and sensible defaults
      const payload: Record<string, any> = {
        // Include all fields from userData
        ...userData,
        currency_code: SettingsService.currencyCode,
        payment_intent: paymentIntent,
        customer_email,
        amount: userData.amount,
        payment_type: userData.paymentType,
        use_credit: false,
      }

      if (usedCredit > 0) {
        // Amount is already in cents, just handle credit conversion
        const amountInCents = userData.amount || 0
        const creditInCents = Math.round(usedCredit * 100)
        payload.amount = Math.max(0, amountInCents - creditInCents)
        payload.use_credit = true
      }

      const { data: response, error } = await fetcher.post(
        toApiUrl('solidgate/update-partial-intent'),
        payload
      )

      if (error.value) {
        throw new Error(
          error.value.message || 'Failed to update payment intent'
        )
      }

      isLoading.value = false
      return response.value
    } catch (e: any) {
      isLoading.value = false
      errors.value = e.message

      // Track error with analytics
      if (gtm?.enabled) {
        gtm.trackEvent({
          event: 'payment_error',
          category: 'Checkout',
          action: 'Update Intent Error',
          label: e.message || 'Unknown error',
        })
      }

      throw e
    }
  }

  /**
   * Subscribe to order status broadcasts
   * Call this when user clicks BUY/PURCHASE button
   */
  const subscribeToOrderBroadcast = (orderId: string) => {
    // Validate input
    if (!orderId || orderId.trim() === '') {
      console.warn('Cannot subscribe to broadcast: invalid order ID')
      return
    }

    // If we already have a subscription for the same order, don't create a duplicate
    if (
      activeOrderSubscription &&
      activeOrderSubscription.orderId === orderId
    ) {
      return
    }

    // Clean up any existing subscription first
    cleanupOrderSubscription()

    // Subscribe to order-specific public channel
    const orderChannel = RealTimeEvents.getChannel('Orders', orderId)

    // Initialize subscription state
    activeOrderSubscription = {
      orderId,
      channelName: orderChannel,
      eventReceived: false,
    }

    RealTimeEvents.subscribe(
      orderChannel,
      RealTimeEvents.Events.OrderStatusUpdate,
      true, // Use public channel
      (event: OrderStatusUpdateEvent) => {
        console.log('Broadcast event received', event)

        // Check that event belongs to the order we're waiting for
        if (
          'one_click_idempotency_key' in event &&
          event.one_click_idempotency_key
        ) {
          if (event.one_click_idempotency_key !== orderId) {
            return // Skip events for other orders
          }
        } else if (event.order_id !== orderId) {
          return // Skip events for other orders
        }

        // Store the event data for later processing
        // Double-check that we still have the right subscription active
        if (
          activeOrderSubscription &&
          activeOrderSubscription.orderId === orderId
        ) {
          activeOrderSubscription.eventData = event
          activeOrderSubscription.eventReceived = true

          // If we have a waiting promise, try to resolve it immediately
          if (activeOrderSubscription.resolvePromise) {
            activeOrderSubscription.resolvePromise(event)
          }
        }
      }
    )
  }

  /**
   * Clean up active order subscription
   */
  const cleanupOrderSubscription = () => {
    if (activeOrderSubscription) {
      RealTimeEvents.unsubscribe(
        activeOrderSubscription.channelName,
        RealTimeEvents.Events.OrderStatusUpdate
      )
      activeOrderSubscription = null
    }
  }

  /**
   * Wait for a broadcast event that indicates order status update
   */
  const waitForOrderStatusBroadcast = async (
    orderId: string,
    timeout = 30000 // 30 seconds
  ) => {
    return new Promise<SolidgateOrderStatusEventResponse>((resolve) => {
      // Ensure we have a subscription before waiting
      if (!orderId) {
        console.warn('Cannot wait for broadcast without a valid order ID')
        resolve({ event: null, timedOut: true })
        return
      }

      if (
        !activeOrderSubscription ||
        activeOrderSubscription.orderId !== orderId
      ) {
        subscribeToOrderBroadcast(orderId)
      }

      // Check if we already have an event from subscription
      if (
        activeOrderSubscription &&
        activeOrderSubscription.orderId === orderId &&
        activeOrderSubscription.eventReceived
      ) {
        const existingEvent = activeOrderSubscription.eventData
        if (
          existingEvent &&
          (ValidPaymentStatuses.includes(existingEvent.status) ||
            FailedPaymentStatuses.includes(existingEvent.status))
        ) {
          // Clean up subscription since we got what we needed
          cleanupOrderSubscription()

          resolve({ event: existingEvent, timedOut: false })
          return
        }
      }

      // Set up a timeout to prevent waiting indefinitely
      const timeoutId = setTimeout(() => {
        // Clean up subscription on timeout
        cleanupOrderSubscription()
        resolve({ event: null, timedOut: true })
      }, timeout)

      // If we have an active subscription but no event yet, wait for it
      if (
        activeOrderSubscription &&
        activeOrderSubscription.orderId === orderId
      ) {
        // Store the resolve function so the subscription callback can call it directly
        activeOrderSubscription.resolvePromise = (
          event: OrderStatusUpdateEvent
        ) => {
          // Validate the event before resolving
          // Check if it's a valid status we should act on
          if (
            ValidPaymentStatuses.includes(event.status) ||
            FailedPaymentStatuses.includes(event.status)
          ) {
            clearTimeout(timeoutId)
            cleanupOrderSubscription()

            resolve({ event, timedOut: false })
          } else {
            // If not a valid status, continue waiting (don't resolve)
          }
        }
      } else {
        // Subscription setup failed (should not happen)
        console.warn(`Unable to subscribe to order ${orderId}`)
        clearTimeout(timeoutId)
        resolve({ event: null, timedOut: true })
      }
    })
  }

  /**
   * Fetch and process instant win tickets for the order
   */
  const getInstantWinTicketsForOrder = async (
    orderId: string
  ): Promise<InstantWinPaymentResult> => {
    try {
      // Fetch the ticket data from the backend
      const ticketData = await fetchInstantWinTickets(orderId)

      const result: InstantWinPaymentResult = {
        success: true,
        data: {
          game_session: ticketData.csi_content.data.game_session,
          played_entries: ticketData.csi_content.data.played_entries,
          allocated_entries: ticketData.csi_content.data.allocated_entries,
        } as GameSessionPageData,
        orderStatus: 'success',
        orderId,
      }

      return result
    } catch (error: any) {
      return {
        success: false,
        data: {
          error:
            error.data.message || `Failed to retrieve instant win tickets.`,
          original_error: error.data,
          order_id: orderId,
        } as any, // Use any for error data structure
        orderStatus: 'error',
        orderId,
      }
    }
  }

  /**
   * Create a timeout error response
   */
  const createTimeoutError = (
    orderId: string,
    paymentType: TPaymentTypeValue,
    userData: any
  ) => {
    const referenceText = `Please contact customer support with order reference: ${orderId}.`

    // Create detailed error message based on payment type
    let errorMessage: string
    let errorData: Record<string, any>

    if (paymentType === PaymentType.InstantWin) {
      errorMessage = `Order status update timed out for instant win purchase. The payment may have been processed, but we could not confirm the tickets for ${userData.instant_win_code}. ${referenceText}`
      errorData = {
        error: errorMessage,
        instant_win_code: userData.instant_win_code,
        order_id: orderId,
      }
    } else if (paymentType === PaymentType.PaymentUpdate) {
      errorMessage = `Payment update timed out. Your payment details may have been updated, but we could not confirm the status. ${referenceText}`
      errorData = {
        error: errorMessage,
        order_id: orderId,
      }
    } else if (paymentType === PaymentType.Subscription) {
      // Subscription flow
      errorMessage = `Order status update timed out for subscription purchase. The payment may have been processed, but we could not confirm the subscription status. ${referenceText}`
      errorData = {
        error: errorMessage,
        subscription_code: userData.product_code,
        order_id: orderId,
      }
    } else {
      // Generic fallback
      errorMessage = `Order status update timed out. The payment may have been processed, but we could not confirm the status. ${referenceText}`
      errorData = {
        error: errorMessage,
        order_id: orderId,
      }
    }

    return {
      success: false,
      timedOut: true,
      data: errorData,
      orderStatus: 'error',
    }
  }

  /**
   * Handle a payment authorized event - tracks the payment and listens for real-time updates
   * for all types of Solidgate payments
   *
   * @param _payment - The payment token
   * @param userData - The user data
   * @returns A promise that resolves to the payment result
   */
  const handlePaymentAuthorized = async (
    _payment: { token: string },
    userData: SolidgatePaymentIntentUserData
  ): Promise<SolidgatePaymentAuthorizedResponse | InstantWinPaymentResult> => {
    // Extract payment details
    const orderId = userData.order_id
    const paymentType = userData.paymentType

    if (!orderId) {
      throw new Error('Order ID is required for payment processing')
    }

    try {
      // Wait for broadcast event
      const { event, timedOut } = await waitForOrderStatusBroadcast(orderId)

      // Handle failed payment statuses
      if (event && FailedPaymentStatuses.includes(event.status)) {
        return {
          success: false,
          data: {
            error:
              event.message || `Payment failed with status: ${event.status}`,
            order_id: orderId,
            details: event,
          },
          orderStatus: event.status,
        }
      }

      // For instant win purchases, we'll try to fetch tickets regardless of timeout
      if (paymentType === PaymentType.InstantWin) {
        // Try to fetch tickets even if broadcast timed out
        const ticketResult = await getInstantWinTicketsForOrder(orderId)

        // If we got valid tickets, return them regardless of timeout
        if (ticketResult.success) {
          // Clear reservation only on successful ticket retrieval
          InstantWinStorage.removeReservation(
            userData.instant_win_code as string
          )
          return ticketResult
        }

        // If we timed out AND couldn't get tickets, then return a timeout error
        if (timedOut) {
          return {
            ...createTimeoutError(orderId, paymentType, userData),
            orderId,
          }
        }

        // Otherwise return the ticket result (which is already an error)
        return ticketResult
      } else if (paymentType === PaymentType.PaymentUpdate) {
        // Payment update flow
        if (timedOut) {
          return {
            ...createTimeoutError(orderId, paymentType, userData),
            orderId,
          }
        }

        const responseStatus = event?.status ?? 'success'
        const responseMessage =
          event?.message ?? 'Payment details updated successfully'
        const responseRedirect = event?.redirect ?? null

        return {
          success: true,
          data: {
            status: responseStatus,
            message: responseMessage,
            order_id: orderId,
          },
          redirect: responseRedirect,
          orderStatus: responseStatus,
          orderId,
        }
      } else if (paymentType === PaymentType.Subscription) {
        // Subscription flow
        if (timedOut) {
          return {
            ...createTimeoutError(orderId, paymentType, userData),
            orderId,
          }
        }

        const responseStatus = event?.status ?? 'success'
        const responseMessage = event?.message ?? 'Payment successful'
        const responseRedirect = event?.redirect ?? null

        return {
          success: true,
          data: {
            status: responseStatus,
            message: responseMessage,
            subscriptionId: userData.product_code || orderId,
          },
          redirect: responseRedirect,
          orderStatus: responseStatus,
          orderId,
        }
      } else {
        // Generic fallback
        if (timedOut) {
          return {
            ...createTimeoutError(orderId, paymentType, userData),
            orderId,
          }
        }

        const responseStatus = event?.status ?? 'success'
        const responseMessage = event?.message ?? 'Payment successful'
        const responseRedirect = event?.redirect ?? null

        return {
          success: true,
          data: {
            status: responseStatus,
            message: responseMessage,
            subscriptionId:
              userData.product_code || userData.product_sku || orderId,
          },
          redirect: responseRedirect,
          orderStatus: responseStatus,
          orderId,
        }
      }
    } catch (error: any) {
      return {
        success: false,
        data: {
          error: error.message || 'Payment processing failed',
          order_id: orderId,
        },
        orderStatus: 'error',
      }
    }
  }

  /**
   * Fetch instant win tickets data from backend using order ID
   */
  const fetchInstantWinTickets = async (orderId: string): Promise<any> => {
    try {
      // Validate orderId is provided
      if (!orderId) {
        throw new Error('Order ID is required to fetch instant win tickets')
      }

      // Make a GET request to the API with order_id as URL parameter
      const { data: response, error } = await fetcher.get(
        toPortalUrl(`instantwin/get_purchase_entries/${orderId}`)
      )

      // Handle API errors more explicitly
      if (error.value) {
        // Include status code if available
        const errorStatus = error.value.status || 500
        const errorMessage =
          error.value.message || 'Failed to fetch instant win tickets'

        // Throw a detailed error to be caught by the caller
        throw new SolidgateException({
          message: errorMessage,
          status: errorStatus,
          isApiError: true,
          orderId,
          originalData: null,
        })
      }

      // Get response data
      const responseData = response.value

      // Validation check to ensure we have valid data
      if (!responseData) {
        throw new SolidgateException({
          message: 'Empty response from instant win tickets endpoint',
          status: 500,
          isApiError: true,
          orderId,
          originalData: null,
        })
      }

      // Check for required success field
      if (!responseData.success) {
        throw new SolidgateException({
          message: 'Invalid response format: missing success field',
          status: 400,
          isApiError: true,
          orderId,
          originalData: responseData,
        })
      }

      // Check for valid success
      if (responseData.success !== true) {
        throw new SolidgateException({
          message: `API returned non-success status: ${responseData.success}`,
          status: 400,
          isApiError: true,
          orderId,
          originalData: responseData,
        })
      }

      return responseData
    } catch (e: any) {
      // Ensure we pass along the error with details
      if (e.isApiError) {
        throw e // Already formatted properly
      } else {
        // Format as a standard error
        throw new SolidgateException({
          message: e.message || 'Failed to fetch instant win tickets',
          status: e.status || 500,
          isApiError: true,
          orderId,
          originalData: null,
        })
      }
    }
  }

  const handlePaymentError = (error: any) => {
    errors.value = error?.message || 'Payment processing failed'
    isLoading.value = false

    // Track error with analytics
    if (gtm?.enabled) {
      gtm.trackEvent({
        event: 'payment_error',
        category: 'Checkout',
        action: 'Payment Error',
        label: error?.message || 'Unknown error',
        order_id: error?.reference || null,
      })
    }
  }

  return {
    mount,
    createOneClickPayment,
    createPaymentIntent,
    createTokenUpdateIntent,
    updatePaymentIntent,
    handlePaymentAuthorized,
    handlePaymentError,
    subscribeToOrderBroadcast,
    cleanupOrderSubscription,
    errors,
    isLoading,
  }
}
